import './ZoomIntervalSelector.css';
import { Divider } from './Divider';
import { ResetZoomButton } from './ResetZoomButton';
import React from 'react';
import { SignalViewerRef } from '@lmt/signal-viewer-ui';

export type PredefinedZoomInterval =
  | '1d'
  | '12h'
  | '6h'
  | '1h'
  | '30m'
  | '10m'
  | '5m'
  | '1m';

export const INTERVAL_MS: Record<PredefinedZoomInterval, number> = {
  '1d': 86_400_000,
  '12h': 43_200_000,
  '6h': 21_600_000,
  '1h': 3_600_000,
  '30m': 1_800_000,
  '10m': 600_000,
  '5m': 300_000,
  '1m': 60_000,
};

interface TimeSelectorProps {
  ref: React.MutableRefObject<SignalViewerRef | null>;
  selectedValue?: PredefinedZoomInterval | null;
  onChange: (value: PredefinedZoomInterval | null) => void;
}

const intervals: PredefinedZoomInterval[] = [
  '1d',
  '12h',
  '6h',
  '1h',
  '30m',
  '10m',
  '5m',
  '1m',
];

export const ZoomIntervalSelector = React.forwardRef<
  SignalViewerRef,
  TimeSelectorProps
>(({ selectedValue, onChange }, ref) => {
  const applyZoomInterval = (intervalMs: number) => {
    const currentRef = ref as React.MutableRefObject<SignalViewerRef | null>;
    const chart = currentRef.current;
    if (!chart) return;

    const currentInterval = chart.getInterval();
    const viewCenterMs = (currentInterval.start + currentInterval.end) / 2;

    const dayWindow = chart.getAxisX()?.getIntervalRestrictions();
    const dayWindowStartMs = dayWindow?.startMin ?? currentInterval.start;
    const dayWindowEndMs = dayWindow?.endMax ?? currentInterval.end;

    const spanMs = Math.min(intervalMs, dayWindowEndMs - dayWindowStartMs);

    // where the span would start if centered on the current view
    const centeredStartMs = viewCenterMs - spanMs / 2;

    const earliestStartMs = dayWindowStartMs;
    const latestStartMs = dayWindowEndMs - spanMs;

    // startMs = clamp(centeredStartMs, earliestStartMs, latestStartMs)
    const startMs = Math.min(
      Math.max(centeredStartMs, earliestStartMs),
      latestStartMs,
    );

    chart.setInterval({
      start: startMs,
      end: startMs + spanMs,
    });
  };

  const handleSelect = (interval: PredefinedZoomInterval) => {
    if (interval === selectedValue) return;

    onChange(interval);
    applyZoomInterval(INTERVAL_MS[interval]);
  };

  const handleReset = () => {
    onChange(null);
    applyZoomInterval(INTERVAL_MS['1d']);
  };

  return (
    <div className='zoom-interval-selector-container'>
      <div className='zoom-interval-selector--btns-group'>
        {intervals.map((interval) => (
          <button
            key={interval}
            onClick={() => handleSelect(interval)}
            className={
              selectedValue === interval
                ? 'zoom-interval-btn--active'
                : 'zoom-interval-btn'
            }
          >
            {interval}
          </button>
        ))}
      </div>

      <Divider />

      <ResetZoomButton onClick={handleReset} />
    </div>
  );
});
